import React, { useState } from 'react';
import { Target, Calendar, TrendingUp, CheckCircle, XCircle, Trash2, Play } from 'lucide-react';
import { updateGoalStatus, deleteGoal } from '../../../services/studentGoalsService';

const GoalCard = ({ goal, onUpdate }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isAbandoning, setIsAbandoning] = useState(false);
  const [error, setError] = useState(null);

  const getGoalTypeLabel = (type) => {
    const labels = {
      overall_accuracy: 'Overall Accuracy',
      play_specific: 'Play-Specific',
      weekly_practice: 'Weekly Practice',
      character_mastery: 'Character Mastery'
    };
    return labels?.[type] || type;
  };

  const getDifficultyColor = (level) => {
    const colors = {
      beginner: 'bg-green-100 text-green-700',
      intermediate: 'bg-yellow-100 text-yellow-700',
      advanced: 'bg-red-100 text-red-700'
    };
    return colors?.[level] || 'bg-gray-100 text-gray-700';
  };

  const progressPercentage = goal?.target_value > 0
    ? Math.min((goal?.current_value / goal?.target_value) * 100, 100)
    : 0;

  const achievedMilestones = goal?.milestones?.filter(m => m?.is_achieved)?.length || 0;
  const totalMilestones = goal?.milestones?.length || 0;

  const daysRemaining = Math.ceil(
    (new Date(goal?.target_date) - new Date()) / (1000 * 60 * 60 * 24)
  );

  const handleComplete = async () => {
    if (isCompleting) return;
    try {
      setIsCompleting(true);
      setError(null);
      await updateGoalStatus(goal?.id, 'completed');
      onUpdate();
    } catch (err) {
      setError(err?.message);
      setIsCompleting(false);
    }
  };

  const handleAbandon = async () => {
    if (isAbandoning) return;
    if (!confirm('Are you sure you want to abandon this goal?')) return;
    
    try {
      setIsAbandoning(true);
      setError(null);
      await updateGoalStatus(goal?.id, 'abandoned');
      onUpdate();
    } catch (err) {
      setError(err?.message);
      setIsAbandoning(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this goal? This action cannot be undone.')) return;
    
    try {
      setIsDeleting(true);
      setError(null);
      await deleteGoal(goal?.id);
      onUpdate();
    } catch (err) {
      setError(err?.message);
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl border-2 border-purple-200 p-6 hover:shadow-xl transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🎯</span>
            <h3 className="text-xl font-bold text-gray-900">
              {getGoalTypeLabel(goal?.goal_type)}
            </h3>
          </div>
          
          {goal?.play && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
              <Play className="w-4 h-4" />
              {goal?.play?.title}
            </div>
          )}
          
          {goal?.character_name && (
            <div className="text-sm text-gray-600 mb-1">
              Character: {goal?.character_name}
            </div>
          )}
          
          {goal?.difficulty_level && (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(goal?.difficulty_level)}`}>
              {goal?.difficulty_level?.charAt(0)?.toUpperCase() + goal?.difficulty_level?.slice(1)}
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleComplete}
            disabled={isCompleting || isAbandoning || isDeleting}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
            title="Mark as complete"
          >
            {isCompleting ? (
              <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={handleAbandon}
            disabled={isCompleting || isAbandoning || isDeleting}
            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Abandon goal"
          >
            {isAbandoning ? (
              <div className="w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting || isCompleting || isAbandoning}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            title="Delete goal"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm font-bold text-purple-600">
            {goal?.current_value?.toFixed(1) || 0} / {goal?.target_value?.toFixed(1) || 0}
            {goal?.goal_type !== 'weekly_practice' && '%'}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {progressPercentage?.toFixed(1)}% Complete
        </div>
      </div>
      {/* Milestones */}
      {totalMilestones > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              Milestones: {achievedMilestones} / {totalMilestones}
            </span>
          </div>
          <div className="flex gap-1">
            {goal?.milestones?.map((milestone, idx) => (
              <div
                key={milestone?.id}
                className={`flex-1 h-2 rounded ${
                  milestone?.is_achieved
                    ? 'bg-green-500' :'bg-gray-200'
                }`}
                title={milestone?.milestone_name}
              />
            ))}
          </div>
        </div>
      )}
      {/* Footer Info */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>
            {daysRemaining > 0 ? `${daysRemaining} days left` : 'Target date passed'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-purple-600">
          <TrendingUp className="w-4 h-4" />
          <span>Keep going!</span>
        </div>
      </div>
    </div>
  );
};

export default GoalCard;